import React, { useCallback, useMemo } from 'react'
import { Control, Controller } from 'react-hook-form'
import { Radio, Stack } from '@mantine/core'

import { useStatePolicies } from '@/components/Event/NewScript/hooks/useStatePolicy'
import { parseStepKey } from '@/components/Event/NewScript/utils/stepKey'
import {
  DEPOSITION_ENDED_OPTIONS_LABEL,
  DepositionResult,
  ScriptStep,
} from '@/constants'
import { Event } from '@/types'

type CanceledEventOptionsProps = {
  canceledFromStep: ScriptStep | string | null
  control: Control<any>
  event: Event
}

const RESCHEDULED = {
  label: 'Rescheduled',
  value: DepositionResult.RESCHEDULED,
  description:
    'The proceeding has not yet officially gone on the record, and the parties agreed to reschedule for a different time.',
}

const CANCELLED = {
  label: DEPOSITION_ENDED_OPTIONS_LABEL[DepositionResult.CANCELLED],
  value: DepositionResult.CANCELLED,
  description:
    'The proceeding has not yet gone on the record, and the parties agreed the proceeding is no longer needed.',
}

const CNA = {
  label: DEPOSITION_ENDED_OPTIONS_LABEL[DepositionResult.CNA],
  value: DepositionResult.CNA,
  description: 'A certificate of non-appearance needs to be issued',
}

const COMPLETED_WITH_NO_SHOW = {
  label: DEPOSITION_ENDED_OPTIONS_LABEL[DepositionResult.COMPLETED_NO_SHOW],
  value: DepositionResult.COMPLETED_NO_SHOW,
  description:
    'The witness did not show and the client wishes to go on the record to record the no show.',
}

export const EventResultOptions: React.FC<CanceledEventOptionsProps> = ({
  canceledFromStep,
  control,
  event,
}) => {
  const { canHaveCna } = useStatePolicies(event)

  const getCanceledFromStep = useCallback(() => {
    if (canceledFromStep) {
      return parseStepKey(canceledFromStep).step
    }

    return null
  }, [canceledFromStep])

  const options = useMemo(() => {
    switch (getCanceledFromStep()) {
      case ScriptStep.ATTENDANCE:
        return [
          RESCHEDULED,
          CANCELLED,
          ...(canHaveCna ? [CNA] : []),
          COMPLETED_WITH_NO_SHOW,
        ]
      case ScriptStep.OPENING:
        return [
          RESCHEDULED,
          CANCELLED,
          ...(canHaveCna ? [CNA] : []),
          COMPLETED_WITH_NO_SHOW,
        ]
      case ScriptStep.QUESTIONING:
        return [CANCELLED, COMPLETED_WITH_NO_SHOW]
      default:
        return [CANCELLED, ...(canHaveCna ? [CNA] : []), COMPLETED_WITH_NO_SHOW]
    }
  }, [getCanceledFromStep, canHaveCna])

  return (
    <Controller
      name="result"
      control={control}
      render={({ field }) => (
        <Radio.Group
          {...field}
          onChange={(value: string) =>
            field.onChange(value as DepositionResult)
          }
        >
          <Stack spacing="sm">
            {options.map(option => (
              <Radio
                styles={{ radio: { cursor: 'pointer' } }}
                key={option.value}
                value={option.value}
                label={option.label}
                description={option.description}
              />
            ))}
          </Stack>
        </Radio.Group>
      )}
    />
  )
}
